import * as React from 'react'

import {
  Box,
  Button,
  Section,
  Text,
  UserAvatar,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { type EnpsEditorFragment, EnpsEditorsSectionDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import Loading from 'modules/core/components/Loading'
import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction from 'modules/core/lib/asyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import AddEditorModal from 'modules/enps/components/AddEditorModal'
import useFormAccessGroup from 'modules/enps/hooks/useAccessGroup'
import type Form from 'modules/enps/store/models/Form'
import formAccessGroupMemberships from 'modules/surveys/store/collections/formAccessGroupMemberships'
import type FormAccessGroupMembership from 'modules/surveys/store/models/FormAccessGroupMembership'

interface ItemProps {
  editor: EnpsEditorFragment
  membership: FormAccessGroupMembership
  form: Form
}

function Item({ editor, membership, form }: ItemProps) {
  const {
    access: { id: currentAccessId },
  } = useCurrentSession()
  const [open, toggle] = useToggle()

  const editorIsCurrentAccess = editor.id === currentAccessId
  const authorId = form.attributes.access_id
  const editorIsAuthor = editor.id === authorId

  const title = i18n.t('enps.form.editors.remove_editor')

  // The current access can not delete themselves and can not the delete the author of the eNPS survey form
  const showDeleteButton = !editorIsCurrentAccess && !editorIsAuthor

  return (
    <Box
      key={editor.id}
      alignItems="center"
      flexDirection="row"
      justifyContent="spaceBetween"
      paddingX="s24"
      paddingY="s32"
      width="full"
      flexGrow
    >
      <Box flexDirection="row" gap="s16" alignItems="center">
        <UserAvatar size="s40" url={buildUrl(editor.avatar?.url)} label={editor.fullName} />
        <Box overflowX="hidden">
          <Text.Normal weight="medium" ellipsis>
            {editor.fullName}
          </Text.Normal>
        </Box>
      </Box>

      {showDeleteButton && (
        <Popover
          attachment="top right"
          targetAttachment="bottom center"
          toggle={toggle}
          open={open}
        >
          <Button
            onClick={toggle(true)}
            type="secondary"
            aria-label="remove editor"
            iconName="delete"
          />
          <PopoverContent
            onClose={toggle(false)}
            options={[
              {
                title: title,
                type: 'wrong',
                onClick: () =>
                  asyncAction(() => membership.destroy({ data: { form_id: form.attributes.id } })),
              },
            ]}
          />
        </Popover>
      )}
    </Box>
  )
}

type MembershipsWithEditor = {
  membership: FormAccessGroupMembership
  editor: EnpsEditorFragment
}

interface ItemsProps {
  form: Form
  members: MembershipsWithEditor[]
}

const Items = ({ form, members }: ItemsProps) => {
  if (!members.length) {
    return (
      <BoxList>
        <Text.Normal>{i18n.t('surveys.form.managers.blank_slate')}</Text.Normal>
      </BoxList>
    )
  }

  return (
    <BoxList>
      {members.map(({ editor, membership }) => (
        <Item key={membership.id} editor={editor} form={form} membership={membership} />
      ))}
    </BoxList>
  )
}

interface Props {
  form: Form
}

function Editors({ form }: Props) {
  const { loading } = useFormAccessGroup(form)
  const memberships = formAccessGroupMemberships.toArray()
  const { data, loading: loadingEditors } = useQuery(EnpsEditorsSectionDocument, {
    variables: {
      accessIds: memberships.map((membership) => membership.attributes.access_id),
    },
    skip: !memberships.length,
  })
  const [openedModal, setOpenedModal] = React.useState(false)

  const isLoading = loading || loadingEditors

  const members = memberships.reduce<MembershipsWithEditor[]>(
    (membershipsWithEditor, membership) => {
      const editor = data?.employees.employeeNames.find(
        (employee) => employee.accessId === membership.attributes.access_id
      )

      if (editor) {
        membershipsWithEditor.push({ membership, editor })
      }

      return membershipsWithEditor
    },
    []
  )

  return (
    <Section
      illustration="people-add"
      title={i18n.t('enps.form.editors.title')}
      description={i18n.t('enps.form.editors.description')}
      action={{
        label: i18n.t('enps.form.editors.add_editor'),
        onClick: () => setOpenedModal(true),
      }}
    >
      {isLoading && <Loading />}
      {!isLoading && openedModal && (
        <AddEditorModal onClose={() => setOpenedModal(false)} form={form} />
      )}
      {!isLoading && <Items form={form} members={members} />}
    </Section>
  )
}

export default observer(Editors)
